import { Divider } from 'components/ui/Divider';
import { FormSectionSimple } from 'components/layout/Section/FormSectionSimple';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { BaseLegalEntityFormFieldNames } from 'shared/enums/baseLegalEntityFormFieldNames';
import { LegalEntityFormFieldNames } from 'features/new-actor/enums/legalEntityFormFieldNames';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useCallback, useMemo } from 'react';
import {
  BorrowerInsuredFormFieldNames,
  BorrowerLegalFormValues
} from '../borrowerInsuredForm';
import { BorrowerBaseLayout } from '../shared/BorrowerBaseLayout';
import { borrowerLegalInputsConfig } from './borrowerLegal.inputs';

interface BorrowerLegalLayoutProps {
  isEdit?: boolean;
  initialTab?: number | string;
}

export const BorrowerLegalLayout: React.FC<BorrowerLegalLayoutProps> = ({
  isEdit = false,
  initialTab
}) => {
  const { t } = useDynamicTranslation([NsKey.Borrower, NsKey.ActorForm]);

  const legalEntityDisabledFields: Array<
    Exclude<keyof BorrowerLegalFormValues, 'id'>
  > = useMemo(
    () => [
      PersonalInformationFieldNames.LastName,
      PersonalInformationFieldNames.FirstName,
      PersonalInformationFieldNames.Civility
    ],
    []
  );

  const renderField = useCallback(
    (fieldName: any) => {
      return (
        <InputFactory<Exclude<keyof BorrowerLegalFormValues, 'id'>>
          config={borrowerLegalInputsConfig}
          fieldName={fieldName}
          disabledFields={isEdit ? legalEntityDisabledFields : undefined}
        />
      );
    },
    [isEdit, legalEntityDisabledFields]
  );

  return (
    <BorrowerBaseLayout initialTab={initialTab}>
      <div className="font-lato flex flex-col gap-4">
        <FormSectionSimple title={t('ACTOR_FORM.LABELS.COMPANY_SECTION')}>
          <>
            <div className="col-span-6">
              {renderField(LegalEntityFormFieldNames.CompanyName)}
            </div>
            <div className="col-span-3">
              {renderField(BaseLegalEntityFormFieldNames.Capital)}
            </div>
            <div className="col-span-3">
              {renderField(BaseLegalEntityFormFieldNames.RcsCity)}
            </div>
            <div className="col-span-3">
              {renderField(BaseLegalEntityFormFieldNames.RegistrationNumber)}
            </div>
            <div className="col-span-3">
              {renderField(BaseLegalEntityFormFieldNames.RcsNumber)}
            </div>
            <div className="col-span-3">
              {renderField(BaseLegalEntityFormFieldNames.LegalReason)}
            </div>
            <div className="col-span-3">
              {renderField(BaseLegalEntityFormFieldNames.Rcp)}
            </div>
            <div className="col-span-3">
              {renderField(BaseLegalEntityFormFieldNames.OriasNumber)}
            </div>
            <div className="col-span-6">
              <Divider />
            </div>
            <div className="col-span-6">
              {renderField(AddressFormFieldNames.Address)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.AdditionalAddress)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.PostalCode)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.City)}
            </div>
            <div className="col-span-3">
              {renderField(AddressFormFieldNames.Country)}
            </div>
          </>
        </FormSectionSimple>
        <FormSectionSimple title={t('ACTOR_FORM.LABELS.CONTACT_SECTION')}>
          <>
            <div className="w-full col-span-6 flex gap-4">
              <div className="w-32">
                {renderField(PersonalInformationFieldNames.Civility)}
              </div>
              <div className="flex-grow">
                {renderField(PersonalInformationFieldNames.LastName)}
              </div>
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.FirstName)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.BirthName)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.BirthCountry)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.Nationality)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.Email)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.Phone)}
            </div>
            <div className="col-span-3">
              {renderField(LegalEntityFormFieldNames.CompanyRole)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.TaxResidenceCountryId)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerInsuredFormFieldNames.Job)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerInsuredFormFieldNames.ProspectType)}
            </div>
            <div className="col-span-3">
              {renderField(PersonalInformationFieldNames.IsPPE)}
            </div>
            <div className="col-span-3">
              {renderField(BorrowerInsuredFormFieldNames.IsFPE)}
            </div>
          </>
        </FormSectionSimple>
      </div>
    </BorrowerBaseLayout>
  );
};
